// src/components/LoopControls.js
import React, { useEffect, useState, useRef } from "react";
import { Range } from "react-range";
import { colors, fontSizes, buttons, layout, rounded } from "../styles/tokens";
import { IconTrash } from "@tabler/icons-react";

const STEP = 0.1;
const PERCENT_MAX = 100;
const TRACK_ROW_HEIGHT = 12;
const BOOKMARK_COLORS = [
  "#60a5fa", "#f87171", "#34d399", "#fbbf24", "#a78bfa", "#f472b6",
  "#38bdf8", "#fb923c", "#4ade80", "#818cf8", "#facc15", "#ec4899"
];

function LoopControls({
  loopSettings,
  onChange,
  duration,
  playerRef,
  bookmarks = [],
  onBookmarkSelect = () => {},
  currentTime = null,
  isPlaying = false,
  setIsPlaying = () => {},
  loopEnabled,
  setLoopEnabled,
  markers = [],
  setMarkers = () => {}
}) {
  const [sliderValues, setSliderValues] = useState([0, 100]);
  const sliderDisabled = markers.length > 0; // disable section handles if markers exist
  const trackRef = useRef(null);             // stable ref to the timeline track

  useEffect(() => {
    if (duration > 0) {
      const startPercent = (loopSettings.start / duration) * PERCENT_MAX;
      const endPercent = (loopSettings.end / duration) * PERCENT_MAX;
      setSliderValues([startPercent, endPercent]);
    }
  }, [loopSettings, duration]);

  // Keep isPlaying in sync with the actual YouTube player state
  useEffect(() => {
    const getState = () => playerRef?.current?.getPlayerState?.();
    const tick = () => {
      const s = getState();
      if (s === 1) setIsPlaying(true);
      else if (s === 2 || s === 0) setIsPlaying(false);
    };
    const id = setInterval(tick, 350);
    tick();
    return () => clearInterval(id);
  }, [playerRef, setIsPlaying]);

  const adjust = (field, delta) => {
    const value = parseFloat((loopSettings[field] + delta).toFixed(2));
    onChange({ ...loopSettings, [field]: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ ...loopSettings, [name]: parseFloat(value) });
  };

  const handleSliderChange = (values) => {
    if (sliderDisabled) return; // ignore slider while markers exist
    setSliderValues(values);
    const [startPercent, endPercent] = values;
    const start = parseFloat(((startPercent / PERCENT_MAX) * duration).toFixed(2));
    const end = parseFloat(((endPercent / PERCENT_MAX) * duration).toFixed(2));
    onChange({ ...loopSettings, start, end });
  };

  const setNow = (field) => {
    if (!playerRef?.current || typeof playerRef.current.getCurrentTime !== "function") return;
    const t = playerRef.current.getCurrentTime();
    if (typeof t === "number") {
      onChange({ ...loopSettings, [field]: parseFloat(t.toFixed(2)) });
    }
  };

  const playFromStart = () => onBookmarkSelect("count-in");

  const togglePlayPause = () => {
    if (!playerRef?.current) return;
    const player = playerRef.current;
    const state = player.getPlayerState?.();
    if (state === 1) {
      player.pauseVideo();
      setIsPlaying(false);
    } else {
      player.playVideo();
      setIsPlaying(true);
    }
  };

  // Convert a clientX to a time using the current track element
  const clientXToTime = (clientX) => {
    const el = trackRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    const px = clientX - rect.left;
    const clamped = Math.max(0, Math.min(px / rect.width, 1));
    return parseFloat((clamped * duration).toFixed(2));
  };

  return (
    <div className={`${layout.sectionSpacing} w-full`}>
      {/* Play Controls */}
      <div className="flex flex-wrap justify-center items-center gap-4 mt-4">
        <button className={buttons.activePurpleSm} onClick={playFromStart}>
          Start
        </button>
        <button
          className={`${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md}`}
          onClick={togglePlayPause}
        >
          {isPlaying ? "Pause" : "Play"}
        </button>
        <label className={`flex items-center gap-2 ${fontSizes.sm}`}>
          <input
            type="checkbox"
            checked={loopEnabled}
            onChange={(e) => setLoopEnabled(e.target.checked)}
          />
          Loop
        </label>
        {markers.length > 0 && (
          <button
            className={`${buttons.whitePurple} ${rounded.md} text-xs px-2 py-1`}
            onClick={() => setMarkers([])}
          >
            <div className="flex items-center gap-2">
              <IconTrash size={18} />
              Clear Dividers
            </div>
          </button>
        )}
      </div>

      {/* Speed Slider */}
      <div className="flex items-center gap-4 mt-4 w-full max-w-md mx-auto mb-4">
        <label className={`${fontSizes.sm} ${colors.tertiary} whitespace-nowrap`}>
          Speed: {loopSettings.speed.toFixed(2)}x
        </label>
        <div className="flex-1">
          <Range
            step={0.05}
            min={0.5}
            max={1}
            values={[loopSettings.speed]}
            onChange={([value]) => {
              onChange({ ...loopSettings, speed: value });
              playerRef?.current?.setPlaybackRate?.(value);
            }}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                className="w-full h-4 rounded overflow-hidden bg-gray-200 relative touch-pan-y select-none"
              >
                {[...Array(11)].map((_, i) => {
                  const percent = i / 10;
                  const lightness = 95 - i * 6;
                  return (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0"
                      style={{
                        left: `${percent * 100}%`,
                        width: `10%`,
                        backgroundColor: `hsl(245, 60%, ${lightness}%)`,
                      }}
                    />
                  );
                })}
                {children}
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "20px",
                  width: "20px",
                  borderRadius: "50%",
                  backgroundColor: "#4f46e5",
                  boxShadow: "0 0 2px rgba(0,0,0,0.5)",
                  zIndex: 120,       // below bookmarks (300), below markers (400), above track
                  touchAction: "none",
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="flex justify-center w-full">
        <div className="w-full max-w-screen-lg">
          <Range
            step={STEP}
            min={0}
            max={PERCENT_MAX}
            values={sliderValues}
            onChange={handleSliderChange}
            renderTrack={({ props, children }) => {
              const rows = [];
              const positioned = bookmarks.map((bm, i) => {
                const left = duration > 0 ? (bm.start / duration) * 100 : 0;
                const width = duration > 0 ? ((bm.end - bm.start) / duration) * 100 : 0;
                let row = 0;
                while (rows[row]?.some((r) => !(bm.end <= r.start || bm.start >= r.end))) {
                  row++;
                }
                if (!rows[row]) rows[row] = [];
                rows[row].push(bm);
                return {
                  key: i,
                  left,
                  width,
                  top: 4 + row * TRACK_ROW_HEIGHT,
                  color: BOOKMARK_COLORS[i % BOOKMARK_COLORS.length],
                  bm
                };
              });

              const maxRow = Math.max(1, rows.length);
              props.style.height = `${TRACK_ROW_HEIGHT * maxRow}px`;
              props.style.marginTop = `${TRACK_ROW_HEIGHT * (maxRow - 1)}px`;

              // Attach our own ref while preserving react-range's internal ref
              const setBothRefs = (el) => {
                trackRef.current = el;
                if (typeof props.ref === "function") props.ref(el);
                else if (props.ref && "current" in props.ref) props.ref.current = el;
              };

              return (
                <div
                  {...props}
                  ref={setBothRefs}
                  className={`w-full relative rounded bg-gray-300 touch-pan-y select-none ${
                    sliderDisabled ? "pointer-events-none" : ""
                  }`}
                >
                  {/* Saved bookmark sections — middle layer */}
                  {positioned.map((b) => (
                    <div
                      key={b.key}
                      role="button"
                      tabIndex={0}
                      className="absolute rounded cursor-pointer opacity-80 hover:opacity-100 z-[300]"
                      style={{
                        left: `${b.left}%`,
                        width: `${b.width}%`,
                        top: `${b.top}px`,
                        height: `${TRACK_ROW_HEIGHT}px`,
                        backgroundColor: b.color,
                        pointerEvents: "auto", // intercept interactions even when parent is pointer-events-none
                      }}
                      title={`${b.bm.title || "Untitled"}: ${b.bm.start}s - ${b.bm.end}s`}
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onClick={() => {
                        onBookmarkSelect?.(b.bm);
                        playerRef?.current?.seekTo?.(b.bm.start);
                        playerRef?.current?.playVideo?.();
                        setIsPlaying?.(true);
                      }}
                    />
                  ))}

                  {/* Loop range highlight (non-interactive) */}
                  <div
                    className="absolute top-0 bottom-0 bg-indigo-500 opacity-30 rounded pointer-events-none"
                    style={{
                      left: `${(sliderValues[0] / PERCENT_MAX) * 100}%`,
                      width: `${((sliderValues[1] - sliderValues[0]) / PERCENT_MAX) * 100}%`,
                      zIndex: 5
                    }}
                  />

                  {/* Draggable Markers — TOP layer, large hit area, thin visible line */}
                  {markers.map((time, idx) => {
                    const percent = (time / duration) * 100;

                    const startDrag = (clientX) => {
                      const newTime = clientXToTime(clientX);
                      setMarkers((prev) =>
                        prev.map((t, i) => (i === idx ? newTime : t)).sort((a, b) => a - b)
                      );
                    };

                    const onPointerDown = (e) => {
                      e.preventDefault();
                      e.stopPropagation(); // don't let Range react
                      startDrag(e.clientX);
                      const move = (ev) => startDrag(ev.clientX);
                      const up = () => {
                        window.removeEventListener("pointermove", move);
                        window.removeEventListener("pointerup", up);
                        window.removeEventListener("pointercancel", up);
                      };
                      window.addEventListener("pointermove", move, { passive: false });
                      window.addEventListener("pointerup", up, { passive: true });
                      window.addEventListener("pointercancel", up, { passive: true });
                    };

                    const onTouchStart = (e) => {
                      if (!e.touches?.length) return;
                      e.stopPropagation();
                      e.preventDefault();
                      startDrag(e.touches[0].clientX);
                      const move = (te) => {
                        te.preventDefault();
                        startDrag(te.touches[0].clientX);
                      };
                      const end = () => {
                        window.removeEventListener("touchmove", move);
                        window.removeEventListener("touchend", end);
                        window.removeEventListener("touchcancel", end);
                      };
                      window.addEventListener("touchmove", move, { passive: false });
                      window.addEventListener("touchend", end, { passive: true });
                      window.addEventListener("touchcancel", end, { passive: true });
                    };

                    const onMouseDown = (e) => {
                      e.stopPropagation();
                      startDrag(e.clientX);
                      const move = (me) => startDrag(me.clientX);
                      const up = () => {
                        document.removeEventListener("mousemove", move);
                        document.removeEventListener("mouseup", up);
                      };
                      document.addEventListener("mousemove", move);
                      document.addEventListener("mouseup", up);
                    };

                    return (
                      <div
                        key={`marker-${idx}`}
                        className="absolute cursor-ew-resize select-none z-[400]"
                        style={{
                          left: `${percent}%`,
                          top: "-12px",
                          height: "calc(120% + 24px)",
                          width: "40px",                 // bigger, easier-to-grab hit area
                          transform: "translateX(-50%)",
                          touchAction: "none",
                          pointerEvents: "auto",
                          WebkitTapHighlightColor: "transparent",
                          background: "transparent",
                          WebkitUserSelect: "none",
                          WebkitTouchCallout: "none",
                          userSelect: "none",
                        }}
                        title={`Marker @ ${time}s`}
                        onPointerDown={onPointerDown}
                        onTouchStart={onTouchStart}
                        onMouseDown={onMouseDown}
                      >
                        {/* visible red line (still 6px) */}
                        <div className="absolute inset-y-0 left-1/2 w-[6px] -translate-x-1/2 bg-red-600 rounded" />
                      </div>
                    );
                  })}

                  {/* Current time indicator */}
                  {typeof currentTime === "number" && (
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-black opacity-70 pointer-events-none"
                      style={{
                        left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                        zIndex: 100
                      }}
                    />
                  )}

                  {children}
                </div>
              );
            }}
            renderThumb={({ props }) => (
              <div
                {...props}
                style={{
                  ...props.style,
                  height: "calc(120% + 24px)",
                  width: "6px",
                  borderRadius: "2px",
                  backgroundColor: "#4521a8ff",
                  position: "absolute",
                  transform: "translate(-50%, -70%)",
                  cursor: sliderDisabled ? "not-allowed" : "ew-resize",
                  boxShadow: "0 0 2px rgba(0,0,0,0.5)",
                  zIndex: 200,        // below bookmarks(300) and markers(400)
                  touchAction: "none",
                  pointerEvents: sliderDisabled ? "none" : "auto",
                  opacity: sliderDisabled ? 0.6 : 1
                }}
              />
            )}
          />
        </div>
      </div>

      {/* Start/End Controls */}
      <div className="bg-gray-100 rounded-xl py-3 px-4 mt-4 shadow-sm w-full">
        <div className="flex flex-wrap justify-center gap-6">
          {["start", "end"].map((field) => (
            <div key={field} className="flex items-center gap-2 flex-wrap justify-center">
              <label className={`${fontSizes.sm} ${colors.tertiary} min-w-[45px] text-center`}>
                {field.charAt(0).toUpperCase() + field.slice(1)}:
              </label>
              <button onClick={() => adjust(field, -0.1)} className={`${fontSizes.sm} px-2 py-1 bg-white border ${rounded.md} hover:bg-gray-100`}>
                −
              </button>
              <input
                type="number"
                name={field}
                step="0.01"
                value={Number(loopSettings[field]).toFixed(2)}
                onChange={handleInputChange}
                className={`w-20 ${fontSizes.sm} border p-1 text-center shadow-inner ${rounded.md}`}
              />
              <button onClick={() => adjust(field, 0.1)} className={`${fontSizes.sm} px-2 py-1 bg-white border ${rounded.md} hover:bg-gray-100`}>
                +
              </button>
              <button onClick={() => setNow(field)} className="text-xs px-3 py-1 bg-blue-100 text-blue-900 rounded hover:bg-blue-200">
                NOW
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LoopControls;
